import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

export interface Contact {
  id: string;
  tenantId: string;
  customerId: string | null;
  partyId: string | null;
  companyName: string | null;
  name: string;
  roleTitle: string | null;
  email: string | null;
  phone: string | null;
  engagementScore: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactInput {
  customerId?: string;
  partyId?: string;
  name: string;
  roleTitle?: string;
  email?: string;
  phone?: string;
  engagementScore?: number;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* RF-013/RF-023: diretório CRM lista Contact, distinto de Party. */
export function useContacts(params?: {
  customerId?: string;
  partyId?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<Contact>>> {
  return useQuery<PaginatedResponse<Contact>>({
    queryKey: ['crm', 'contacts', params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<Contact>>('/contacts', {
        params: {
          ...(params?.customerId ? { customerId: params.customerId } : {}),
          ...(params?.partyId ? { partyId: params.partyId } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 100),
        },
      }),
  });
}

function useInvalidateContacts() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] });
  };
}

export function useCreateContact() {
  const invalidate = useInvalidateContacts();
  return useMutation<Contact, unknown, ContactInput, unknown>({
    mutationFn: (input: ContactInput) => apiClient.post<Contact>('/contacts', input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidateContacts();
  return useMutation<void, unknown, string, unknown>({
    mutationFn: async (id: string) => apiClient.delete<void>(`/contacts/${id}`),
    onSuccess: () => invalidate(),
  });
}
